import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Instagram } from 'lucide-react';
import { FOUNDERS } from '../../data/content';
import { LineRevealOnView, Reveal } from './Reveal';

const EASE = [0.22, 1, 0.36, 1];

const FounderCard = ({ person, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <Reveal
      delay={index * 0.12}
      className={`col-span-12 md:col-span-4 ${index === 1 ? 'md:mt-20' : ''} ${
        index === 2 ? 'md:mt-10' : ''
      }`}
    >
      <div className="group">
        <div ref={ref} className="relative overflow-hidden aspect-[3/4] bg-teal/5">
          <motion.img
            src={person.image}
            alt={person.name}
            style={{ y }}
            className="editorial-img absolute inset-0 w-full h-[112%] object-cover"
          />
          <span className="absolute top-4 left-4 font-serif italic text-cream/80 text-lg mix-blend-difference">
            0{index + 1}
          </span>
        </div>

        <div className="mt-6">
          <div className="flex items-baseline justify-between border-b border-teal/15 pb-3">
            <h3 className="font-serif text-3xl md:text-4xl text-teal tracking-tight">
              {person.name}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-[10px] tracking-[0.28em] uppercase text-klein font-semibold">
                {person.role}
              </span>
              <a
                href={person.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid={`founder-ig-${person.name.toLowerCase()}`}
                aria-label={`${person.name} on Instagram`}
                className="text-teal/50 hover:text-klein transition-colors duration-300"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>
          <p className="mt-4 text-sm uppercase tracking-[0.12em] text-teal/70 font-medium">
            {person.titles}
          </p>
          <p className="mt-3 text-base font-light leading-relaxed text-ink/75">{person.bio}</p>
        </div>
      </div>
    </Reveal>
  );
};

export const Founders = () => (
  <section id="founders" className="py-10 md:py-14 scroll-mt-20">
    <div className="max-w-[1500px] mx-auto px-6 md:px-12">
      <div className="grid grid-cols-12 gap-y-5 mb-7 md:mb-10">
        <div className="col-span-12 md:col-span-3">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-klein font-semibold">
              {FOUNDERS.overline}
            </span>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-9">
          <LineRevealOnView
            lines={["WE COULDN'T FIND THIS PLACE.", 'SO WE ARE BUILDING IT.']}
            lineClassName="font-serif uppercase text-teal text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-[-0.01em]"
            stagger={0.12}
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8 md:gap-10">
        {FOUNDERS.people.map((p, i) => (
          <FounderCard key={p.name} person={p} index={i} />
        ))}
      </div>
    </div>
  </section>
);
